import classNames from 'classnames';
import { HTMLProps, forwardRef, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DatePickerHeader from './DatePickerHeader';

export type ReactDatePickerValue = [Date | undefined, Date | undefined];

interface DatePickerProps {
  onChange: (event: {
    target: { value: ReactDatePickerValue; name: string };
  }) => void;
  value: ReactDatePickerValue;
  label?: string;
  error?: string;
  name: string;
  disabled?: boolean;
}

interface DatePickerCustomInputProps extends HTMLProps<HTMLParagraphElement> {
  value?: string;
}

const DatePickerCustomInput = forwardRef<
  HTMLInputElement,
  DatePickerCustomInputProps
>(({ value, onClick, className, onChange }, ref) => {
  const formattedValue = useMemo(() => {
    if (!value) return '';

    const [startDate, endDate] = value
      .split('-')
      .map((date: string) => date.trim());

    if (!startDate || !endDate || startDate === endDate) return startDate;

    return `${startDate} to ${endDate}`;
  }, [value]);

  return (
    <input
      className={classNames(className, '!m-0 bg-white')}
      onClick={onClick}
      ref={ref}
      data-test-id='datepicker-value'
      value={formattedValue}
      onChange={onChange}
    />
  );
});

export const DatePicker = forwardRef(
  (
    { onChange, value, label, name, error, ...rest }: DatePickerProps,
    ref: React.LegacyRef<HTMLDivElement>,
  ) => {
    return (
      <div
        data-test-id='datepicker-container'
        ref={ref}
        className='relative w-full mb-5'
      >
        {label && (
          <label
            data-test-id='datepicker-label-test-id'
            className='w-full text-sm font-medium leading-5 text-gray-800'
          >
            {label}
          </label>
        )}
        <ReactDatePicker
          onChange={(dates: [Date | null, Date | null]) => {
            if (dates[0]) {
              onChange({
                target: { value: dates as ReactDatePickerValue, name },
              });
            }
          }}
          startDate={value[0]}
          endDate={value[1] || undefined}
          className={`hover:border-gray-200 focus:!ring-gray-200 focus:!border-gray-200 mb-2 mt-2 block w-full rounded border border-gray-200 p-[10px] placeholder-gray-500 outline-none transition-all h-[42px] text-sm leading-5 text-gray-600`}
          {...rest}
          calendarClassName='rounded-xl !border !border-solid'
          showPopperArrow={false}
          selectsRange
          dateFormat='MM/dd/yyyy'
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            changeYear,
          }) => (
            <DatePickerHeader
              date={date}
              decreaseMonth={decreaseMonth}
              increaseMonth={increaseMonth}
              changeYear={changeYear}
            />
          )}
          customInput={<DatePickerCustomInput />}
        />
        {error && (
          <h4
            data-test-id='datepicker-error-test-id'
            className={classNames(
              'absolute bottom-[-20px] mt-4 text-xs leading-5 text-red-600',
            )}
          >
            {error}
          </h4>
        )}
      </div>
    );
  },
);
